import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useAuth } from '../AuthProvider'
import { useI18n } from '../../i18n'
import type { AiShellMode } from './types'
import {
  useDefendSphereAiConversation,
  type DefendSphereAiConversationApi,
} from './useDefendSphereAiConversation'
import './defendsphere-ai.css'

type DefendSphereAiContextValue = {
  enabled: boolean
  mode: AiShellMode
  openPanel: () => void
  openWorkspace: () => void
  close: () => void
  collapseToPanel: () => void
  togglePanel: () => void
  conversation: DefendSphereAiConversationApi
}

const DefendSphereAiContext = createContext<DefendSphereAiContextValue | null>(null)

export function DefendSphereAiProvider({ children }: { children: ReactNode }) {
  const { token, isAuthenticated } = useAuth()
  const { t } = useI18n()
  // AI-4: DefendSphere AI is always available for authenticated users (RBAC still enforced by API).
  const enabled = Boolean(isAuthenticated)
  const [mode, setMode] = useState<AiShellMode>('closed')
  const conversation = useDefendSphereAiConversation(token, enabled, t)

  const openPanel = useCallback(() => setMode('panel'), [])
  const openWorkspace = useCallback(() => setMode('workspace'), [])
  const close = useCallback(() => {
    if (conversation.pendingDisclosure?.required && conversation.disclosureBusy) return
    setMode('closed')
  }, [conversation.pendingDisclosure, conversation.disclosureBusy])
  const collapseToPanel = useCallback(() => setMode('panel'), [])
  const togglePanel = useCallback(() => {
    setMode((prev) => (prev === 'closed' ? 'panel' : prev === 'panel' ? 'closed' : 'panel'))
  }, [])

  useEffect(() => {
    if (mode === 'closed') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (conversation.pendingDisclosure?.required) return
      if (mode === 'workspace') {
        setMode('panel')
        return
      }
      setMode('closed')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, conversation.pendingDisclosure])

  const value = useMemo(
    () => ({
      enabled,
      mode,
      openPanel,
      openWorkspace,
      close,
      collapseToPanel,
      togglePanel,
      conversation,
    }),
    [enabled, mode, openPanel, openWorkspace, close, collapseToPanel, togglePanel, conversation],
  )

  return <DefendSphereAiContext.Provider value={value}>{children}</DefendSphereAiContext.Provider>
}

export function useDefendSphereAi(): DefendSphereAiContextValue {
  const ctx = useContext(DefendSphereAiContext)
  if (!ctx) {
    throw new Error('useDefendSphereAi must be used within DefendSphereAiProvider')
  }
  return ctx
}

export function useDefendSphereAiOptional(): DefendSphereAiContextValue | null {
  return useContext(DefendSphereAiContext)
}
